// Cookie consent banner for Firebase Analytics. Shown ONLY when a consent
// decision is actually pending: analytics is configured, no stored choice
// exists, and the visitor's geography requires opt-in (EU/EEA/UK) or is
// unknown. Everyone else — JP and other disclosure-based jurisdictions, or a
// visitor who already chose — never sees it automatically. The modal owns focus.
// "Cookie choices" is a footer link (ConsentSettingsLink) beside Privacy/Terms
// that reopens this dialog; it never covers page content. Screens without a
// public footer (auth, partner application, docs) render the same control via
// StandaloneConsentSettings at the end of their content. Escape defers the choice.

import { useEffect, useId, useRef, useState } from "react";

import type { Translate } from "../i18n";
import { suspendAnalyticsForSensitiveLocation } from "../lib/analytics";
import { consentBannerNeeded, getConsent, setConsent } from "../lib/consent";
import {
  isAnalyticsConfigured,
  setFirebaseAnalyticsCollectionAllowed
} from "../lib/firebase";

const OPEN_CONSENT_SETTINGS_EVENT = "tinkr:open-consent-settings";

/** Reopens the consent dialog mounted by ConsentBanner. */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS_EVENT));
}

/**
 * Footer control for changing a stored consent choice. Rendered next to the
 * Privacy/Terms links; absent when analytics is not configured (nothing to
 * consent to, and ConsentBanner renders no dialog).
 */
export function ConsentSettingsLink({ t }: { t: Translate }) {
  if (!isAnalyticsConfigured()) return null;
  return <button className="consent-settings-link" type="button" onClick={openConsentSettings}>
    {t("consent.aria")}
  </button>;
}

/**
 * Quiet "Cookie choices" control for public screens that render no footer
 * (auth, partner application, docs portal). Same label and handler as the
 * footer link; null when analytics is not configured.
 */
export function StandaloneConsentSettings({ t }: { t: Translate }) {
  if (!isAnalyticsConfigured()) return null;
  return <div className="consent-settings-standalone">
    <ConsentSettingsLink t={t} />
  </div>;
}

export function ConsentBanner({ t }: { t: Translate }) {
  const [visible, setVisible] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const choiceMade = useRef(false);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    // No config → analytics can never run → nothing to consent to.
    if (!isAnalyticsConfigured()) return;
    let cancelled = false;
    void consentBannerNeeded().then((needed) => {
      if (!cancelled && !choiceMade.current && getConsent() === null && needed) setVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const open = (): void => setVisible(true);
    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, open);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const element = dialog.current;
    const previousFocus = document.activeElement;
    if (!element) return;
    // Older browsers/webviews still get usable, non-modal consent actions.
    // A partial dialog implementation may expose showModal but throw on use.
    let modal = false;
    try {
      if (typeof element.showModal === "function") {
        element.showModal();
        modal = true;
      }
    } catch { /* Fall back to an in-flow prompt without blocking the app. */ }
    element.setAttribute("open", "");
    element.toggleAttribute("data-modal", modal);
    element.setAttribute("aria-modal", String(modal));
    return () => {
      try { if (typeof element.close === "function") element.close(); } catch { /* Partial implementation. */ }
      element.removeAttribute("open");
      element.removeAttribute("data-modal");
      element.removeAttribute("aria-modal");
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [visible]);

  if (!isAnalyticsConfigured()) return null;

  const choose = (choice: "all" | "essential"): void => {
    choiceMade.current = true;
    setConsent(choice);
    setVisible(false);
    // SDK failures must never prevent a visitor from closing the dialog.
    try {
      const sensitive = suspendAnalyticsForSensitiveLocation(window.location.href);
      setFirebaseAnalyticsCollectionAllowed(choice === "all" && !sensitive);
    } catch { /* Analytics is optional; the consent decision is already saved. */ }
  };

  return <>
    <dialog ref={dialog} role="dialog" className="consent-dialog" aria-labelledby={titleId}
      aria-describedby={descriptionId} onCancel={() => setVisible(false)}>
      <h2 id={titleId}>{t("consent.aria")}</h2>
      <p id={descriptionId} className="consent-banner__copy">{t("consent.body")}</p>
      <div className="consent-banner__actions">
        <button
          className="consent-banner__decline"
          onClick={() => choose("essential")}
          type="button"
        >
          {t("consent.decline")}
        </button>
        <button className="consent-banner__accept" onClick={() => choose("all")} type="button">
          {t("consent.accept")}
        </button>
      </div>
    </dialog>
  </>;
}
